/**
 * Criativos — image/video creative analysis powered by GPT-5.6 vision.
 * Unified layout: drop area on top, list of past analyses below.
 * Each list item navigates to /criativos/:id detail page.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AppLayout } from "@/components/layout/AppLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { PageStateGate } from "@/components/states/PageStateGate";
import { usePageTitle } from "@/hooks/usePageTitle";
import { toast } from "sonner";
import { friendlyError, toastError } from "@/lib/userMessages";
import {
  ArrowClockwise,
  CaretRight,
  CloudArrowUp,
  FilmReel,
  ImageSquare,
  Sparkle,
  Warning,
  X,
} from "@phosphor-icons/react";
import { useAnalyzeCreative } from "@/features/criativos/hooks/useAnalyzeCreative";
import { useAnalyzeCreativeFromTranscript } from "@/features/criativos/hooks/useAnalyzeCreativeFromTranscript";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { useCriativos } from "@/features/criativos/hooks/useCriativos";
import { useDeleteCriativo } from "@/features/criativos/hooks/useDeleteCriativo";
import { useRenameVideo } from "@/features/videos/hooks/useRenameVideo";
import { useHasTranscriptionKey } from "@/features/byok/hooks/useHasTranscriptionKey";
import { TranscriptionKeyGate } from "@/features/byok/components/TranscriptionKeyGate";
import { DeleteEntityButton } from "@/components/shared/DeleteEntityButton";
import { InlineEditText } from "@/components/shared/InlineEditText";
import { BulkActionsBar } from "@/components/shared/BulkActionsBar";
import { Checkbox } from "@/components/ui/checkbox";
import {
  VEICULACAO_OPTIONS,
  type Veiculacao,
} from "@/features/criativos/types";
import { cn } from "@/lib/utils";

const STAGE_LABEL: Record<string, string> = {
  idle: "",
  preparing: "Preparando arquivo…",
  "extracting-audio": "Extraindo áudio do vídeo…",
  transcribing: "Transcrevendo áudio…",
  analyzing: "Analisando criativo com IA…",
  done: "Concluído",
  error: "Falhou",
};

export default function Videos() {
  usePageTitle("Analisar Criativo");
  const navigate = useNavigate();

  const fileInputRef = useRef<HTMLInputElement>(null);
  const [file, setFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [veiculacao, setVeiculacao] = useState<Veiculacao>("Instagram Reels");
  const [objetivo, setObjetivo] = useState("");
  const [label, setLabel] = useState("");
  const [isDragging, setIsDragging] = useState(false);
  const [inputMode, setInputMode] = useState<"upload" | "paste">("upload");
  const [pastedTranscript, setPastedTranscript] = useState("");
  const MIN_TRANSCRIPT_CHARS = 50;

  const analyze = useAnalyzeCreative();
  const analyzePaste = useAnalyzeCreativeFromTranscript();
  const activeAnalyze = inputMode === "paste" ? analyzePaste : analyze;
  const transcriptionKey = useHasTranscriptionKey();
  const list = useCriativos();
  const deleteCriativo = useDeleteCriativo();
  const renameVideo = useRenameVideo();
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [selectionMode, setSelectionMode] = useState(false);
  const [bulkDeleting, setBulkDeleting] = useState(false);

  useEffect(() => {
    setSelectedIds((prev) => {
      const validIds = new Set((list.data ?? []).map((row) => row.id));
      const next = new Set<string>();
      prev.forEach((id) => {
        if (validIds.has(id)) next.add(id);
      });
      return next;
    });
  }, [list.data]);

  const isVideo = useMemo(
    () =>
      file
        ? file.type.startsWith("video/") || /\.(mp4|mov|webm|m4v)$/i.test(file.name)
        : false,
    [file],
  );

  function pickFile(f: File | null) {
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    if (f) {
      setFile(f);
      setPreviewUrl(URL.createObjectURL(f));
      if (!label) setLabel(f.name.replace(/\.[^.]+$/, ""));
    } else {
      setFile(null);
      setPreviewUrl(null);
    }
    analyze.reset();
  }

  function handleSubmit() {
    if (inputMode === "paste") {
      const trimmed = pastedTranscript.trim();
      if (trimmed.length < MIN_TRANSCRIPT_CHARS) {
        toast.error(`A transcrição precisa ter pelo menos ${MIN_TRANSCRIPT_CHARS} caracteres.`);
        return;
      }
      if (!label.trim()) {
        toast.error("Adicione um título para o criativo.");
        return;
      }
      analyzePaste.mutate(
        {
          label: label.trim(),
          veiculacao,
          objetivo: objetivo.trim() || undefined,
          transcriptText: trimmed,
        },
        {
          onError: (err) => {
            toastError(err, "Não foi possível analisar a transcrição. Tente novamente.");
          },
          onSuccess: (data) => {
            toast.success("Análise concluída!");
            setObjetivo("");
            setLabel("");
            setPastedTranscript("");
            navigate(`/criativos/${data.criativoId}`);
          },
        },
      );
      return;
    }

    if (!file) {
      toast.error("Selecione um criativo (imagem ou vídeo) primeiro.");
      return;
    }
    if (
      isVideo &&
      !transcriptionKey.canTranscribe &&
      !transcriptionKey.isLoading
    ) {
      toast.error("Configure uma API key da OpenAI em Perfil → API Keys para analisar vídeos com áudio.");
      return;
    }
    analyze.mutate(
      {
        file,
        veiculacao,
        objetivo: objetivo.trim() || undefined,
        label: label.trim() || undefined,
      },
      {
        onError: (err) => {
          toastError(err, "Não foi possível analisar o criativo. Tente novamente.");
        },
        onSuccess: (data) => {
          toast.success("Análise concluída!");
          pickFile(null);
          setObjetivo("");
          setLabel("");
          navigate(`/criativos/${data.criativoId}`);
        },
      },
    );
  }

  function handleDrop(e: React.DragEvent<HTMLDivElement>) {
    e.preventDefault();
    setIsDragging(false);
    const f = e.dataTransfer.files?.[0];
    if (f) pickFile(f);
  }

  function toggleSelection(id: string, checked: boolean) {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (checked) next.add(id);
      else next.delete(id);
      return next;
    });
  }

  async function handleBulkDelete() {
    if (selectedIds.size === 0) return;
    setBulkDeleting(true);
    const ids = Array.from(selectedIds);
    const results = await Promise.allSettled(ids.map((id) => deleteCriativo.mutateAsync(id)));
    const success = results.filter((r) => r.status === "fulfilled").length;
    const failed = results.length - success;
    if (success > 0) toast.success(`${success} criativo(s) excluído(s).`);
    if (failed > 0) toast.error(`${failed} criativo(s) não puderam ser excluídos.`);
    setSelectedIds(new Set());
    setBulkDeleting(false);
  }

  return (
    <AppLayout>
      <PageStateGate empty={{ title: "Ainda sem criativos", description: "Envie uma imagem ou vídeo para analisar" }}>
        <div className="space-y-6 animate-fade-in">
          <div>
            <h1 className="text-2xl font-semibold text-foreground">
              Analisar <span className="accent-text">Criativo</span>
            </h1>
            <p className="text-muted-foreground mt-1">
              Imagem ou vídeo · IA detecta objetivo e julga conforme veiculação
            </p>
          </div>

          {/* Pre-flight: warn when video selected (upload mode) but no transcription key.
              Pasted transcript mode bypasses Whisper, so the gate is hidden. */}
          {file &&
            isVideo &&
            inputMode === "upload" &&
            !transcriptionKey.isLoading &&
            !transcriptionKey.canTranscribe && <TranscriptionKeyGate.Inline />}

          <Tabs
            value={inputMode}
            onValueChange={(v) => {
              setInputMode(v as "upload" | "paste");
              analyze.reset();
              analyzePaste.reset();
            }}
          >
            <TabsList className="grid w-full grid-cols-2 sm:w-auto sm:inline-flex">
              <TabsTrigger value="upload">Enviar mídia</TabsTrigger>
              <TabsTrigger value="paste">Colar transcrição</TabsTrigger>
            </TabsList>

            <TabsContent value="paste" className="mt-4">
              <div className="bg-card border border-border/50 rounded-xl p-6 space-y-4">
                <p className="text-sm text-muted-foreground">
                  Sem mídia: a IA analisa o criativo apenas pelo texto colado.
                  Útil quando você já tem o roteiro/legenda pronto e não quer gastar
                  com transcrição.
                </p>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div>
                    <Label className="text-sm">Veiculação</Label>
                    <Select
                      value={veiculacao}
                      onValueChange={(v) => setVeiculacao(v as Veiculacao)}
                      disabled={analyzePaste.isPending}
                    >
                      <SelectTrigger className="mt-1">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {VEICULACAO_OPTIONS.map((v) => (
                          <SelectItem key={v} value={v}>
                            {v}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-sm">Título do criativo</Label>
                    <Input
                      className="mt-1"
                      value={label}
                      onChange={(e) => setLabel(e.target.value)}
                      placeholder="Ex: Reels hérnia disco — sinais"
                      disabled={analyzePaste.isPending}
                    />
                  </div>
                </div>

                <div>
                  <Label className="text-sm">
                    Objetivo (opcional — IA detecta se vazio)
                  </Label>
                  <Textarea
                    className="mt-1 min-h-[60px]"
                    value={objetivo}
                    onChange={(e) => setObjetivo(e.target.value)}
                    placeholder="Ex: Converter visualizações em agendamentos"
                    disabled={analyzePaste.isPending}
                  />
                </div>

                <div>
                  <Label className="text-sm">Transcrição</Label>
                  <Textarea
                    className="mt-1 min-h-[160px] font-mono text-xs"
                    value={pastedTranscript}
                    onChange={(e) => setPastedTranscript(e.target.value)}
                    placeholder="Cole o texto completo do criativo aqui..."
                    disabled={analyzePaste.isPending}
                  />
                  <p
                    className={cn(
                      "text-xs mt-1",
                      pastedTranscript.trim().length >= MIN_TRANSCRIPT_CHARS
                        ? "text-muted-foreground"
                        : "text-destructive",
                    )}
                  >
                    {pastedTranscript.trim().length} caracteres
                    {pastedTranscript.trim().length < MIN_TRANSCRIPT_CHARS &&
                      ` — mínimo ${MIN_TRANSCRIPT_CHARS}`}
                  </p>
                </div>

                <Button
                  className="w-full gap-2 glow-primary-sm"
                  onClick={handleSubmit}
                  disabled={
                    analyzePaste.isPending ||
                    !label.trim() ||
                    pastedTranscript.trim().length < MIN_TRANSCRIPT_CHARS
                  }
                >
                  {analyzePaste.isPending ? (
                    <>
                      <ArrowClockwise weight="bold" className="w-4 h-4 animate-spin" />
                      Analisando transcrição…
                    </>
                  ) : (
                    <>
                      <Sparkle weight="duotone" className="w-4 h-4" />
                      Analisar transcrição
                    </>
                  )}
                </Button>

                {analyzePaste.error && (
                  <div className="flex items-start gap-3 bg-destructive/15 border border-destructive/30 rounded-lg p-4">
                    <Warning
                      weight="duotone"
                      className="w-5 h-5 text-destructive shrink-0 mt-0.5"
                    />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-destructive">
                        Erro ao analisar transcrição
                      </p>
                      <p className="text-xs text-muted-foreground mt-0.5 break-words">
                        {friendlyError(
                          analyzePaste.error,
                          "Não foi possível analisar a transcrição. Tente novamente.",
                        )}
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </TabsContent>

            <TabsContent value="upload" className="mt-4">
              {/* Drop zone / upload form */}
              <div className="bg-card border border-border/50 rounded-xl p-6 space-y-4">
            {!file ? (
              <div
                role="button"
                tabIndex={0}
                onClick={() => fileInputRef.current?.click()}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") fileInputRef.current?.click();
                }}
                onDragOver={(e) => {
                  e.preventDefault();
                  if (!isDragging) setIsDragging(true);
                }}
                onDragLeave={() => setIsDragging(false)}
                onDrop={handleDrop}
                className={cn(
                  "border-2 border-dashed rounded-xl p-12 text-center cursor-pointer transition-all",
                  isDragging
                    ? "border-primary bg-primary/10 scale-[1.01]"
                    : "border-border/60 hover:border-primary/60 hover:bg-primary/5",
                )}
              >
                <CloudArrowUp
                  weight="duotone"
                  className={cn(
                    "w-12 h-12 mx-auto mb-3 transition-colors",
                    isDragging ? "text-primary" : "text-primary/80",
                  )}
                />
                <p className="text-base font-medium text-foreground">
                  {isDragging
                    ? "Solte para enviar"
                    : "Arraste o criativo aqui ou clique para selecionar"}
                </p>
                <p className="text-xs text-muted-foreground mt-1">
                  JPG, PNG · MP4, MOV, WebM · até 200MB
                </p>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="flex items-start gap-4 bg-muted/30 border border-border/40 rounded-lg p-3">
                  <div className="w-28 h-28 rounded-lg overflow-hidden bg-black/40 shrink-0 flex items-center justify-center">
                    {isVideo ? (
                      previewUrl ? (
                        /* WCAG 1.2.2 — local file-picker preview, not published media.
                           Whisper transcription happens server-side after submit;
                           the transcript appears in the analysis surface. */
                        <video
                          src={previewUrl}
                          className="w-full h-full object-cover"
                          muted
                          playsInline
                          controls
                          aria-label={`Pré-visualização do vídeo ${file?.name ?? "carregado"}`}
                        />
                      ) : (
                        <FilmReel
                          weight="duotone"
                          className="w-8 h-8 text-muted-foreground"
                        />
                      )
                    ) : previewUrl ? (
                      // A7-A11y — alt="" because the file name is shown in the
                      // adjacent <p>; the image is decorative duplication
                      // (WCAG 1.1.1: don't repeat info already in DOM text).
                      <img
                        src={previewUrl}
                        alt=""
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <ImageSquare
                        weight="duotone"
                        className="w-8 h-8 text-muted-foreground"
                      />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-foreground truncate">
                      {file.name}
                    </p>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      {(file.size / 1024 / 1024).toFixed(1)} MB ·{" "}
                      {isVideo ? "Vídeo" : "Imagem"}
                    </p>
                    <Badge variant="outline" className="mt-2 text-xs">
                      {isVideo ? "Áudio + transcrição" : "Imagem"}
                    </Badge>
                  </div>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => pickFile(null)}
                    disabled={analyze.isPending}
                    aria-label="Remover arquivo"
                  >
                    <X weight="bold" className="w-4 h-4" />
                  </Button>
                </div>

                {isVideo && (
                  <div role="note" className="text-xs text-muted-foreground bg-muted/30 border border-border/40 rounded-md p-2">
                    Vídeo sem legendas — a transcrição será gerada pela IA após o envio e ficará disponível na análise.
                  </div>
                )}

                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div>
                    <Label className="text-sm">Veiculação</Label>
                    <Select
                      value={veiculacao}
                      onValueChange={(v) => setVeiculacao(v as Veiculacao)}
                      disabled={analyze.isPending}
                    >
                      <SelectTrigger className="mt-1">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {VEICULACAO_OPTIONS.map((v) => (
                          <SelectItem key={v} value={v}>
                            {v}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-sm">Rótulo (opcional)</Label>
                    <Input
                      className="mt-1"
                      value={label}
                      onChange={(e) => setLabel(e.target.value)}
                      placeholder="Ex: Reels hérnia disco v2"
                      disabled={analyze.isPending}
                    />
                  </div>
                </div>

                <div>
                  <Label className="text-sm">
                    Objetivo (opcional — IA detecta se vazio)
                  </Label>
                  <Textarea
                    className="mt-1 min-h-[60px]"
                    value={objetivo}
                    onChange={(e) => setObjetivo(e.target.value)}
                    placeholder="Ex: Converter visualizações em agendamentos de consulta"
                    disabled={analyze.isPending}
                  />
                </div>

                <Button
                  className="w-full gap-2 glow-primary-sm"
                  onClick={handleSubmit}
                  disabled={
                    analyze.isPending ||
                    (isVideo &&
                      !transcriptionKey.isLoading &&
                      !transcriptionKey.canTranscribe)
                  }
                >
                  {analyze.isPending ? (
                    <>
                      <ArrowClockwise weight="bold" className="w-4 h-4 animate-spin" />
                      {STAGE_LABEL[analyze.stage] ?? "Analisando…"}
                    </>
                  ) : (
                    <>
                      <Sparkle weight="duotone" className="w-4 h-4" />
                      Analisar Criativo
                    </>
                  )}
                </Button>

                {analyze.isPending && <StageIndicator stage={analyze.stage} />}

                {analyze.error && (
                  <div className="flex items-start gap-3 bg-destructive/15 border border-destructive/30 rounded-lg p-4">
                    <Warning
                      weight="duotone"
                      className="w-5 h-5 text-destructive shrink-0 mt-0.5"
                    />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-destructive">
                        Erro ao analisar criativo
                      </p>
                      <p className="text-xs text-muted-foreground mt-0.5 break-words">
                        {friendlyError(analyze.error, "Não foi possível analisar o criativo. Tente novamente.")}
                      </p>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      className="shrink-0 text-destructive border-destructive/30"
                      onClick={handleSubmit}
                    >
                      Tentar novamente
                    </Button>
                  </div>
                )}
              </div>
            )}
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*,video/*"
              className="hidden"
              onChange={(e) => pickFile(e.target.files?.[0] ?? null)}
            />
              </div>
            </TabsContent>
          </Tabs>

          {/* List */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-sm font-medium text-muted-foreground">
                Análises recentes
                {list.data && list.data.length > 0 && (
                  <Badge variant="outline" className="ml-2 text-xs">
                    {list.data.length}
                  </Badge>
                )}
              </h2>
            </div>
            <BulkActionsBar
              totalCount={list.data?.length ?? 0}
              selectedCount={selectedIds.size}
              allSelected={(list.data?.length ?? 0) > 0 && selectedIds.size === (list.data?.length ?? 0)}
              selectionMode={selectionMode}
              deleting={bulkDeleting}
              entityPluralLabel="criativos"
              onToggleSelectionMode={(enabled) => {
                setSelectionMode(enabled);
                if (!enabled) setSelectedIds(new Set());
              }}
              onToggleSelectAll={(checked) => {
                if (!checked) {
                  setSelectedIds(new Set());
                  return;
                }
                setSelectedIds(new Set((list.data ?? []).map((row) => row.id)));
              }}
              onClearSelection={() => setSelectedIds(new Set())}
              onConfirmDelete={handleBulkDelete}
            />

            {list.isPending && (
              <div className="flex items-center justify-center py-8">
                <ArrowClockwise
                  weight="bold"
                  className="w-5 h-5 animate-spin text-muted-foreground"
                />
              </div>
            )}
            {list.isError && (
              <div className="bg-destructive/15 border border-destructive/30 rounded-lg p-4">
                <p className="text-sm text-destructive">
                  {friendlyError(list.error, "Não foi possível carregar suas análises. Tente atualizar a página.")}
                </p>
              </div>
            )}
            {list.data && list.data.length === 0 && (
              <div className="text-center py-8 text-muted-foreground">
                <p className="text-sm">Nenhuma análise ainda. Envie seu primeiro criativo acima.</p>
              </div>
            )}
            <div className="space-y-2">
              {list.data?.map((row) => (
                <div
                  key={row.id}
                  role="button"
                  tabIndex={0}
                  onClick={() => navigate(`/criativos/${row.id}`)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      navigate(`/criativos/${row.id}`);
                    }
                  }}
                  className="w-full text-left bg-card border border-border/50 rounded-xl p-4 hover:bg-secondary/20 hover:border-primary/40 transition-colors group cursor-pointer"
                >
                  <div className="flex items-center gap-3">
                    {selectionMode ? (
                      <div className="shrink-0" onClick={(e) => e.stopPropagation()}>
                        <Checkbox
                          checked={selectedIds.has(row.id)}
                          onCheckedChange={(v) => toggleSelection(row.id, Boolean(v))}
                          aria-label="Selecionar criativo"
                          disabled={bulkDeleting}
                        />
                      </div>
                    ) : null}
                    <div className="p-2 rounded-lg bg-primary/10 shrink-0">
                      {row.media_kind === "video" ? (
                        <FilmReel weight="duotone" className="w-4 h-4 text-primary" />
                      ) : (
                        <ImageSquare weight="duotone" className="w-4 h-4 text-primary" />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <InlineEditText
                        value={row.label || ""}
                        onSave={async (newName) => {
                          try {
                            await renameVideo.mutateAsync({ id: row.id, label: newName });
                            toast.success("Renomeado com sucesso.");
                          } catch (err) {
                            toastError(err, "Não foi possível renomear. Tente novamente.");
                            throw err;
                          }
                        }}
                        placeholder="(sem rótulo)"
                        className="text-sm font-medium text-foreground"
                        disabled={bulkDeleting}
                      />
                      <p className="text-xs text-muted-foreground mt-0.5">
                        {row.veiculacao} ·{" "}
                        {new Date(row.created_at).toLocaleDateString("pt-BR", {
                          day: "2-digit",
                          month: "short",
                          hour: "2-digit",
                          minute: "2-digit",
                        })}
                      </p>
                      <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
                        {row.analysis_payload?.score_geral != null && (
                          <Badge variant="outline" className="text-[11px]">
                            {row.analysis_payload.score_geral.toFixed(1)}/10
                          </Badge>
                        )}
                        {row.objetivo_detectado && (
                          <span className="text-xs text-muted-foreground truncate max-w-[200px]">
                            <span className="font-medium">Obj:</span>{" "}
                            {row.objetivo_detectado}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      <DeleteEntityButton
                        entityLabel="criativo"
                        confirmDescription="A análise será removida do seu histórico permanentemente."
                        onConfirm={async () => {
                          try {
                            await deleteCriativo.mutateAsync(row.id);
                            toast.success("Criativo excluído.");
                          } catch (err) {
                            toastError(err, "Não foi possível excluir. Tente novamente.");
                            throw err;
                          }
                        }}
                        disabled={bulkDeleting}
                        data-testid={`criativo-delete-${row.id}`}
                      />
                      <CaretRight
                        weight="bold"
                        className="w-4 h-4 text-muted-foreground group-hover:text-primary transition-colors"
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </PageStateGate>
    </AppLayout>
  );
}

function StageIndicator({ stage }: { stage: string }) {
  const stages = [
    { key: "preparing", label: "Prep" },
    { key: "extracting-audio", label: "Áudio" },
    { key: "transcribing", label: "Transcrição" },
    { key: "analyzing", label: "Análise" },
  ];
  const currentIdx = stages.findIndex((s) => s.key === stage);
  return (
    <div className="flex gap-2">
      {stages.map((s, i) => {
        const done = i < currentIdx;
        const active = i === currentIdx;
        return (
          <div
            key={s.key}
            className={cn(
              "flex-1 rounded-md px-2 py-1 text-[10px] text-center border",
              done
                ? "bg-primary/20 border-primary/40 text-primary"
                : active
                  ? "bg-primary/10 border-primary/60 text-primary animate-pulse"
                  : "bg-muted/30 border-border/40 text-muted-foreground",
            )}
          >
            {s.label}
          </div>
        );
      })}
    </div>
  );
}
